import type { ReactNode } from 'react';
import { useId } from 'react';
import { cn } from '../cn.js';
import { HelpTip } from './help-tip.js';
import { Label } from './label.js';

export interface FormFieldProps {
  label: string;
  description?: string;
  /** Optional contextual help revealed by a "?" next to the label. */
  help?: ReactNode;
  error?: string;
  required?: boolean;
  className?: string;
  children: (args: { id: string; invalid: boolean; describedBy: string | undefined }) => ReactNode;
}

export function FormField({
  label,
  description,
  help,
  error,
  required,
  className,
  children,
}: FormFieldProps) {
  const id = useId();
  const descId = `${id}-desc`;
  const errId = `${id}-err`;
  const describedBy = error ? errId : description ? descId : undefined;
  const invalid = Boolean(error);

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center gap-1.5">
        <Label htmlFor={id}>
          {label}
          {required ? (
            <span aria-hidden className="ml-0.5 text-destructive">
              *
            </span>
          ) : null}
        </Label>
        {help ? <HelpTip label={`About ${label}`}>{help}</HelpTip> : null}
      </div>
      {children({ id, invalid, describedBy })}
      {description && !error ? (
        <p id={descId} className="text-xs text-muted-foreground">
          {description}
        </p>
      ) : null}
      {error ? (
        <p id={errId} className="text-xs font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
